import React from 'react';
import { motion } from 'framer-motion';
import { CalendarClock, MapPin } from 'lucide-react';
import { Avatar, AvatarFallback } from './Avatar';
import { Badge } from './Badge';
import { EmiratiBadge } from './EmiratiBadge';
import { ApplicantTierBadge } from './ApplicantTierBadge';
import { PlatformIcon } from './PlatformIcon';
import type { Applicant } from '../types/applicant';
import { formatAED, formatDate, getInitials } from '../utils/expert';
import { applicantScore, applicantStatusStyles, getApplicantTier } from '../utils/applicant';
type ApplicantCardProps = {
  applicant: Applicant;
  index: number;
  onOpen: (id: string) => void;
};
export function ApplicantCard({
  applicant,
  index,
  onOpen
}: ApplicantCardProps) {
  const tier = getApplicantTier(applicant);
  const score = applicantScore(applicant);
  return <motion.li initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    delay: index * 0.03
  }} data-id="element-21" data-mp="2vqqtnj5t1gm9">
      <button type="button" onClick={() => onOpen(applicant.id)} className="flex w-full flex-col gap-4 rounded-xl border border-dashed bg-card p-4 text-left transition-colors hover:border-foreground/30 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 md:flex-row md:items-center" data-id="element-22" data-mp="2qsfhh1s1eu0v">
        <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-23" data-mp="30c2o7l9xtm2x">
          <Avatar size="lg" data-id="element-24" data-mp="2mlpg9ix1q7dl"><AvatarFallback className="font-medium" data-id="element-25" data-mp="2ml7mqu1rq79d">{getInitials(applicant.name)}</AvatarFallback></Avatar>
          <div className="min-w-0" data-id="element-26" data-mp="2rdr1huc6z8fr">
            <div className="flex flex-wrap items-center gap-1.5" data-id="element-27" data-mp="2rf20zhyltu12">
              <span className="truncate text-sm font-semibold" data-id="element-28" data-mp="2cpdzogqfavdc">{applicant.name}</span>
              {applicant.isEmirati && <EmiratiBadge compact data-id="element-29" data-mp="2hno1ha6nku7x" />}
            </div>
            <p className="truncate text-xs text-muted-foreground" data-id="element-30" data-mp="23mv32ed8dezj">{applicant.title} · {applicant.organization}</p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-id="element-31" data-mp="27nx2owb27t21">
              <span className="inline-flex items-center gap-1" data-id="element-32" data-mp="e7atp3wiv98y"><MapPin className="size-3" aria-hidden="true" data-id="element-33" data-mp="e82edjokzmkg" />{applicant.location}</span>
              <span className="inline-flex items-center gap-1" data-id="element-34" data-mp="9dg79spj8g66"><CalendarClock className="size-3" aria-hidden="true" data-id="element-35" data-mp="4fog50wpoy31" />Applied {formatDate(applicant.appliedOn)}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 md:w-56" data-id="element-36" data-mp="3vkwku9lmr472">
          {applicant.fields.map(f => <Badge key={f} variant="secondary" data-id="element-37" data-mp="x30y0xrxghq0">{f}</Badge>)}
        </div>

        <div className="flex items-center gap-5 md:w-auto" data-id="element-38" data-mp="s6k0xe2r2t0s">
          <div className="text-right" data-id="element-39" data-mp="n6egahuwe6s7">
            <p className="text-[11px] text-muted-foreground" data-id="element-40" data-mp="n5crlakylls0">Score</p>
            <p className="text-sm font-semibold tabular-nums" data-id="element-41" data-mp="j63y86ttzpwz">{score}</p>
          </div>
          <div className="hidden text-right lg:block" data-id="element-42" data-mp="37xodlm4n5l2e">
            <p className="text-[11px] text-muted-foreground" data-id="element-43" data-mp="37v26fnlnx7kp">Proposed rate</p>
            <p className="text-sm font-semibold tabular-nums" data-id="element-44" data-mp="33ury44aee52o">{formatAED(applicant.proposedDailyRate)}</p>
          </div>
          <div className="hidden gap-1 xl:flex" data-id="element-45" data-mp="1jbfvbittmk7n">
            {applicant.accounts.map(a => <PlatformIcon key={a.platform} platform={a.platform} className="size-6" data-id="element-46" data-mp="1nfnl7jc0m67g" />)}
          </div>
          <div className="flex w-32 flex-col items-end gap-1.5" data-id="element-47" data-mp="1scnvk6fwdv4d">
            <ApplicantTierBadge tier={tier} data-id="element-48" data-mp="1x574gq3n422u" />
            <Badge variant="outline" className={applicantStatusStyles[applicant.status]} data-id="element-49" data-mp="1x70e8e9jv2f2">{applicant.status}</Badge>
          </div>
        </div>
      </button>
    </motion.li>;
}